import axios from "axios";
import type { TFunction } from "i18next";

import type { ValidationFailure } from "../models/Shared/SaveResult";
import {
  MAX_REQUEST_BODY_SIZE_FRAGMENT,
  REQUEST_BODY_TOO_LARGE_FRAGMENT,
  REQUEST_PAYLOAD_TOO_LARGE,
  SAVE_ERROR_PROPERTY_NAME,
} from "./constants";

type ApiErrorResponseBody = {
  code?: string;
};

function readAxiosResponseBody(error: unknown): ApiErrorResponseBody | undefined {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  return error.response?.data as ApiErrorResponseBody | undefined;
}

function isRequestPayloadTooLargeAxiosError(error: unknown) {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const body = readAxiosResponseBody(error);
  const isPayloadTooLargeByStatus =
    error.response?.status === REQUEST_PAYLOAD_TOO_LARGE.status;
  const isPayloadTooLargeByCode =
    body?.code === REQUEST_PAYLOAD_TOO_LARGE.code;

  return isPayloadTooLargeByStatus || isPayloadTooLargeByCode;
}

function isRequestPayloadTooLargeStringRejection(error: unknown) {
  if (typeof error !== "string") {
    return false;
  }

  const lower = error.toLowerCase();

  return (
    lower.includes(REQUEST_BODY_TOO_LARGE_FRAGMENT) ||
    lower.includes(MAX_REQUEST_BODY_SIZE_FRAGMENT)
  );
}

function isRequestPayloadTooLargeObjectRejection(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("code" in error)) {
    return false;
  }

  return (error as { code?: string }).code === REQUEST_PAYLOAD_TOO_LARGE.code;
}

function createSaveValidationFailure(
  message: string
): ValidationFailure {
  return {
    propertyName: SAVE_ERROR_PROPERTY_NAME,
    message,
  };
}

export function isRequestPayloadTooLargeSaveRejection(error: unknown) {
  return (
    isRequestPayloadTooLargeAxiosError(error) ||
    isRequestPayloadTooLargeStringRejection(error) ||
    isRequestPayloadTooLargeObjectRejection(error)
  );
}

export function getSaveCatchValidationFailures(
  error: unknown,
  t: TFunction
): ValidationFailure[] {
  if (isRequestPayloadTooLargeSaveRejection(error)) {
    return [createSaveValidationFailure(t("common.savePayloadTooLarge"))];
  }

  return [createSaveValidationFailure(t("common.systemError"))];
}
